import { CircularProgress, styled } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useState } from "react";
import apiCall from "../../../../utils/apiCall";
import SettingsModal from '../Modal/settingsCustomModal';
import { ReactComponent as RoleIcon } from '../../../../svg/adminRoleIcon.svg';
import { ReactComponent as Arrow } from '../../../../svg/arrow.svg';
import UpdateScoreType from "./UpdateScoreType";
import ScoreTypeOptions from "./ScoreTypeOptions";
import ScoreTypeDetails from "./ScoreTypeDetails";

interface ScoreTypeResponse {
  StatusCode: number;
  Message: string;
  Data: ScoreType[];
}

interface ScoreType {
  ScoreTypeId: number;
  Name: string;
  RouteId: number;
  RouteName: string;
}

const ScoreTypeContent = () => {
    const [openScoreTypeDetails, setOpenScoreTypeDetails] = useState(false);
    const [openUpdateScoreTypeModal, setOpenUpdateScoreTypeModal] = useState(false);
    const [openDeleteScoreTypeModal, setOpenDeleteScoreTypeModal] = useState(false);
    const [scoreType, setScoreType] = useState('');
    const [scoreTypeId, setScoreTypeId] = useState(0);
    const [routeId, setRouteId] = useState(0);
    const [routeName, setRouteName] = useState('');

    const handleOpenScoreTypeDetails = () => setOpenScoreTypeDetails(true);
    const handleCloseScoreTypeDetails = () => setOpenScoreTypeDetails(false);

    const handleOpenUpdateScoreTypeModal = () => setOpenUpdateScoreTypeModal(true);
    const handleCloseUpdateScoreTypeModal = () => setOpenUpdateScoreTypeModal(false);

    const handleOpenDeleteScoreTypeModal = () => setOpenDeleteScoreTypeModal(true);
    const handleCloseDeleteScoreTypeModal = () => setOpenDeleteScoreTypeModal(false);

    const handleDelete = () => {
        handleCloseScoreTypeDetails();
        handleOpenDeleteScoreTypeModal();
    };
    
    const handleUpdate = () => {
        handleCloseScoreTypeDetails();
        handleOpenUpdateScoreTypeModal();
    };

    const getScoreTypes = async (): Promise<ScoreTypeResponse> => {
        return (await apiCall().get('/SetUp/GetAllScoreTypes')).data; 
    };

    const { data, isLoading, isError, error } = useQuery(['get-score-type'], getScoreTypes, {
        retry: 0,
        keepPreviousData: true,
        enabled: true,
    });

    const getScoreTypeMessage = error as AxiosError;
    const getScoreTypeErrorMessage = getScoreTypeMessage?.message;

    const handleOpen = (scoreType: ScoreType) => {
        setScoreType(scoreType?.Name);
        setScoreTypeId(scoreType?.ScoreTypeId);
        setRouteId(scoreType?.RouteId);
        setRouteName(scoreType?.RouteName);
        handleOpenScoreTypeDetails();
    };

    return (
        <>
            {isLoading ? (
                <CircularProgress 
                    sx={{
                        fontSize: '1rem',
                        marginInline: 'auto',
                        display: 'block',
                        marginBlock: '2rem',
                    }}
                />
            ) : isError ? (
                <StyledError>{getScoreTypeErrorMessage}</StyledError>
            ) : (
            <StyledContainer>
                <TotalWrapper>
                    <span>Total</span>
                    <TotalNumberStyle>{data?.Data?.length}</TotalNumberStyle>
                </TotalWrapper>
                {data?.Data && data?.Data?.length > 0 &&
                    data?.Data.map((data, index) => (
                    <RoleWrapper onClick={() => handleOpen(data)} key={index}>
                        <div>
                            <RoleIconWrapper><RoleIcon /></RoleIconWrapper>
                            <RoleText>{data?.Name}</RoleText>
                            <Active>Active</Active>
                            <DateWrapper>{data?.RouteName}</DateWrapper>
                        </div>
                        <ArrowIconWrapper>
                            <Arrow />
                        </ArrowIconWrapper>
                    </RoleWrapper>
                ))}

                <SettingsModal 
                    open={openScoreTypeDetails}
                    cancel={handleCloseScoreTypeDetails}
                    maxWidth='712px'
                    header={scoreType}
                    content={<ScoreTypeDetails ScoreType={scoreType} RouteName={routeName} />}
                    card={<ScoreTypeOptions 
                        handleUpdate={handleUpdate} 
                        handleDelete={handleDelete}
                    />}
                />

                <SettingsModal
                    open={openUpdateScoreTypeModal}
                    cancel={handleCloseUpdateScoreTypeModal}
                    maxWidth='480px'
                    header='Update Score Type'
                    showrightIcon={false}
                    content={<UpdateScoreType 
                        scoreTypeId={scoreTypeId} 
                        handleClose={handleCloseUpdateScoreTypeModal} 
                        scoreType={scoreType}
                        routeId={routeId}
                    />}   
                />

                {/* <SettingsModal
                    open={openDeleteCountryModal}
                    cancel={handleCloseDeleteCountryModal}
                    maxWidth='653px'
                    header='Delete Country'
                    showrightIcon={false}
                    content={<DeleteCountry 
                        countryId={countryId} 
                        countryName={countryName} 
                        handleClose={handleCloseDeleteCountryModal}
                    />}   
                /> */}
            </StyledContainer>
            )}
        </> 
    )
}

export default ScoreTypeContent;

const StyledContainer = styled('div')`
    margin-block: 3.8rem;
    margin-inline: 3.5rem;
`

const TotalNumberStyle = styled('span')`
    background-color: #F1F3F4;
    border-radius: 0.2rem;
    margin-inline-start: 0.4rem;
    padding-inline: 0.2rem;
`

const TotalWrapper = styled('div')`
    color: #4E5A62;
    font-weight: 500;
`

const RoleWrapper = styled('div')`
    display: flex;
    justify-content: space-between;
    border: 0.1rem solid #E1E1E1;
    margin-block: 0.6rem;
    padding-inline: 1.2rem;
    padding-block: 1.2rem;
    border-radius: 0.5rem;
    width: 100%;
    text-align: left;
    background-color: transparent;
`

const RoleIconWrapper = styled('span')`
    background-color: #FFF4DE;
    border-radius: 0.1rem;
    padding-inline: 0.4rem;
`

const RoleText = styled('span')`
    font-weight: 600;
    font-size: 16px;
    margin-inline: 0.8rem;
`

const Active = styled('span')`
    font-size: 14px;
    font-weight: 500;
    background-color: #E0FAEE;
    border-radius: 0.4rem;
    padding-inline: 0.5rem;
    color: #11643C;
`

const DateWrapper = styled('div')`
    margin-block-start: 0.8rem;
`

const ArrowIconWrapper = styled('div')`
    display: flex;
    flex-direction: column;
    justify-content: center;
`

const StyledError = styled('p')`
  color: red;
  font-size: 1.4rem;
  display: flex;
`